import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { SEO } from "@/components/SEO";
import { Clock, ArrowLeft } from "lucide-react";

/**
 * Minimal but nice markdown renderer — handles headings (#/##/###),
 * paragraphs, bold, italic, links, ordered/unordered lists, and inline code.
 * No dependency. Content is trusted (recruiter-authored).
 */
const renderMarkdown = (src) => {
  const lines = (src || "").split("\n");
  const blocks = [];
  let paragraph = [];
  let list = null;
  const flushPara = () => {
    if (paragraph.length) {
      blocks.push({ type: "p", text: paragraph.join(" ") });
      paragraph = [];
    }
  };
  const flushList = () => {
    if (list) { blocks.push(list); list = null; }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) { flushPara(); flushList(); continue; }

    // Headings
    const h = line.match(/^(#{1,3})\s+(.*)$/);
    if (h) { flushPara(); flushList(); blocks.push({ type: `h${h[1].length}`, text: h[2] }); continue; }

    // Bulleted list
    const ul = line.match(/^[\-\*]\s+(.*)$/);
    if (ul) {
      flushPara();
      if (!list || list.type !== "ul") { flushList(); list = { type: "ul", items: [] }; }
      list.items.push(ul[1]);
      continue;
    }
    // Ordered list
    const ol = line.match(/^\d+\.\s+(.*)$/);
    if (ol) {
      flushPara();
      if (!list || list.type !== "ol") { flushList(); list = { type: "ol", items: [] }; }
      list.items.push(ol[1]);
      continue;
    }

    flushList();
    paragraph.push(line);
  }
  flushPara();
  flushList();
  return blocks;
};

const inline = (text) => {
  // Order: links → bold → italic → inline code
  let s = text
    .replace(/`([^`]+)`/g, '<code class="bg-white/5 border border-white/10 px-1.5 py-0.5 rounded text-[13px] text-[#06B6D4] font-mono">$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong class="text-slate-100">$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em class="italic">$1</em>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-[#06B6D4] hover:underline">$1</a>');
  return { __html: s };
};

const Block = ({ b }) => {
  switch (b.type) {
    case "h1": return <h1 className="font-display text-3xl font-semibold mt-8 mb-3" dangerouslySetInnerHTML={inline(b.text)} />;
    case "h2": return <h2 className="font-display text-2xl font-semibold mt-10 mb-3" dangerouslySetInnerHTML={inline(b.text)} />;
    case "h3": return <h3 className="font-display text-xl font-semibold mt-8 mb-3" dangerouslySetInnerHTML={inline(b.text)} />;
    case "p":  return <p className="text-slate-300 leading-relaxed my-4" dangerouslySetInnerHTML={inline(b.text)} />;
    case "ul": return (
      <ul className="list-disc ml-6 my-4 space-y-1.5 text-slate-300">
        {b.items.map((it, i) => <li key={i} dangerouslySetInnerHTML={inline(it)} />)}
      </ul>
    );
    case "ol": return (
      <ol className="list-decimal ml-6 my-4 space-y-1.5 text-slate-300">
        {b.items.map((it, i) => <li key={i} dangerouslySetInnerHTML={inline(it)} />)}
      </ol>
    );
    default: return null;
  }
};

const useArticleSchema = (post) => {
  useEffect(() => {
    if (!post) return;
    const data = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.excerpt,
      datePublished: post.published_at,
      dateModified: post.published_at,
      author: { "@type": "Person", name: post.author_name || "Axiony" },
      publisher: {
        "@type": "Organization",
        name: "Axiony",
        logo: { "@type": "ImageObject", url: "https://axiony.co.in/favicon.svg" },
      },
      mainEntityOfPage: `https://axiony.co.in/blog/${post.slug}`,
      keywords: (post.tags || []).join(", "),
    };
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-article", "true");
    s.textContent = JSON.stringify(data);
    document.head.appendChild(s);
    return () => s.remove();
  }, [post]);
};

export const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  useArticleSchema(post);

  if (notFound) return (
    <div className="max-w-3xl mx-auto px-6 py-20 text-center">
      <div className="text-slate-400">Post not found.</div>
      <Link to="/blog" className="text-[#06B6D4] hover:underline mt-4 inline-block">Back to blog</Link>
    </div>
  );
  if (!post) return <div className="max-w-3xl mx-auto px-6 py-14 text-slate-500">Loading…</div>;

  const blocks = renderMarkdown(post.body);

  return (
    <article className="max-w-3xl mx-auto px-6 py-14" data-testid="blog-post">
      <SEO
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
      />
      <Link to="/blog" className="mono-label flex items-center gap-1.5 text-slate-500 hover:text-white transition-colors">
        <ArrowLeft className="w-3 h-3" /> back to blog
      </Link>
      <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mt-6 leading-[1.1]">
        {post.title}
      </h1>
      <div className="flex items-center gap-4 mt-4 text-xs text-slate-500 font-mono uppercase">
        <span>{new Date(post.published_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {post.reading_minutes} min read</span>
        <span>{post.author_name}</span>
      </div>
      {post.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-4">
          {post.tags.map((t) => (
            <span key={t} className="text-[10px] px-2 py-0.5 rounded border border-white/10 text-slate-400 font-mono uppercase">{t}</span>
          ))}
        </div>
      )}
      <div className="mt-10">
        {blocks.map((b, i) => <Block key={i} b={b} />)}
      </div>
    </article>
  );
};
